import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Copy, Trash2, Pencil, RefreshCw, CheckCircle, Wand2, FileText, Languages, Lightbulb, Share2, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/PageHeader";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const KINDS = [
  { key: "write", icon: Pencil }, { key: "rewrite", icon: RefreshCw }, { key: "correct", icon: CheckCircle },
  { key: "improve", icon: Wand2 }, { key: "summary", icon: FileText }, { key: "translate", icon: Languages },
  { key: "ideas", icon: Lightbulb }, { key: "social", icon: Share2 }, { key: "marketing", icon: Megaphone },
];

export default function Create() {
  const { t } = useI18n();
  const { refreshUser } = useAuth();
  const [kind, setKind] = useState("write");
  const [prompt, setPrompt] = useState("");
  const [result, setResult] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = () => api.get("/create/items").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const gen = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/create/generate", { kind, prompt, title: prompt.slice(0, 40) });
      setResult(data.result); load(); refreshUser();
    } catch (e) { toast.error(e.response?.status === 402 ? e.response.data?.detail?.message : (e.response?.data?.detail || t("common.error"))); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader title={t("create.title")} subtitle={t("create.subtitle")} />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {KINDS.map((k) => {
              const Icon = k.icon;
              return (
                <button key={k.key} onClick={() => setKind(k.key)} data-testid={`create-kind-${k.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${kind === k.key ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}>
                  <Icon className="h-3.5 w-3.5" />{t(`create.${k.key}`)}
                </button>
              );
            })}
          </div>
          <div className="rounded-2xl border bg-card p-5 space-y-3">
            <Textarea rows={8} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={t("create.prompt")} data-testid="create-prompt" />
            <Button onClick={gen} disabled={loading} className="rounded-full w-full" data-testid="create-generate-btn"><Sparkles className="h-4 w-4 mr-2" />{loading ? t("common.loading") : t("common.generate")}</Button>
          </div>
          {result && (
            <div className="rounded-2xl border bg-primary/5 p-5" data-testid="create-result">
              <div className="flex justify-between items-center mb-2"><span className="text-sm font-semibold">{t("create.result")}</span>
                <button onClick={() => { navigator.clipboard.writeText(result); toast.success(t("common.copied")); }}><Copy className="h-4 w-4 text-muted-foreground" /></button></div>
              <p className="text-sm whitespace-pre-wrap">{result}</p>
            </div>
          )}
        </div>
        <div>
          <h3 className="font-display font-semibold mb-3">{t("create.myCreations")}</h3>
          {items.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-creations">{t("create.noCreations")}</p>}
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.id} className="rounded-xl border bg-card p-3" data-testid="creation-card">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs uppercase tracking-wider text-primary font-semibold">{t(`create.${it.kind}`)}</span>
                  <div className="flex gap-2">
                    <button onClick={() => { navigator.clipboard.writeText(it.result); toast.success(t("common.copied")); }}><Copy className="h-4 w-4 text-muted-foreground hover:text-foreground" /></button>
                    <button onClick={async () => { await api.delete(`/create/items/${it.id}`).catch(() => {}); load(); }}><Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" /></button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-4">{it.result}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
